import { flow } from "fp";
import { QueryKey } from "react-query";

import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Log from "~/Log";
import * as Driver from "~/Driver";
import { Request } from "~/Request";
import { Time } from "~/Time";

export const toEndpoint = ({
  company,
  interval,
  assetIDs,
}: Time.Interval.Stopped & {
  readonly company: Data.Reference.Reference;
  readonly assetIDs?: Company.Assets.Filter.AssetIDs;
}) => {
  const assetIDQueryString =
    assetIDs && assetIDs.length > 0 ? `&asset_ids=${assetIDs.join(",")}` : "";
  return `/elogs/companies/${
    company.id
  }/logs/unassigned?start_date=${Time.DateTime.toISO(
    interval.start
  )}&end_date=${Time.DateTime.toISO(interval.stop)}${assetIDQueryString}`;
};

export namespace Query {
  export const tag = () => "Unassigned Logs" as const;
  export const key = ({
    company,
    interval,
    branchIDs,
    assetIDs,
  }: Time.Interval.Stopped & {
    readonly company: Data.Reference.Reference;
    readonly branchIDs: Company.Branch.Branches.BranchIDs;
    readonly assetIDs?: Company.Assets.Filter.AssetIDs;
  }): QueryKey => [
    tag(),
    [company.id,
    Time.DateTime.toISOWithoutTime(interval.start),
    Time.DateTime.toISOWithoutTime(interval.stop),
    branchIDs.join(","),
    (assetIDs ?? []).join(",")].join(":"),
  ];

  export const request = ({
    company,
    interval,
    branchIDs,
    assetIDs,
  }: Time.Interval.Stopped & {
    readonly company: Data.Reference.Reference;
    readonly branchIDs: Company.Branch.Branches.BranchIDs;
    readonly assetIDs?: Company.Assets.Filter.AssetIDs
  }): Request.Fn<Log.Unassigned.UnassignedLogs> => {
    const endpoint =
      toEndpoint({ company, interval, assetIDs }) +
      Driver.Request.buildQueryString({ branchIDs, prefix: "&" });
    return flow(
      Request.fromConfig({
        endpoint,
      }),
      Request.Expect.status(200),
      Request.Expect.json(Log.Unassigned.Response.QueryResponse.runtime()),
      Request.onSuccess(Log.Unassigned.Response.QueryResponse.decode)
    );
  };

  export const prefetch = () => {
    const interval = Log.Unassigned.Interval.initial();
    Log.Unassigned.use(interval);
  };
}
